import { useState } from "react";

import { BarChart } from "../components/BarChart";
import { Note } from "../components/Note";
import { formatCount } from "../utils/chart";
import { formatChange, getBucketTitle, getCurrentNote, getPreviousLabel, getTickLabel } from "../utils/stats";
import { t } from "../i18n/strings";
import { useUsersStats } from "../hooks/useUsersStats";
import type { StatsBucket, StatsPeriod, UsersStats } from "../api/types";

const PERIODS: { value: StatsPeriod; label: "stats.period.day" | "stats.period.month" | "stats.period.year" }[] = [
  { value: "day", label: "stats.period.day" },
  { value: "month", label: "stats.period.month" },
  { value: "year", label: "stats.period.year" },
];

/** What the bars of a chart are: the number itself, or how it moved since the bucket before. */
type Mode = "sum" | "change";

const MODES: { value: Mode; label: "stats.mode.sum" | "stats.mode.change" }[] = [
  { value: "sum", label: "stats.mode.sum" },
  { value: "change", label: "stats.mode.change" },
];

/** One mode of a chart: the line under its title and what its bars and popovers say. */
interface Series {
  total: string;
  value: (bucket: StatsBucket) => number;
  /** The first line of the popover, the number of the bar in words. */
  valueLabel: (bucket: StatsBucket) => string;
  /** A line under it, for the numbers a change was counted from. */
  detail?: (bucket: StatsBucket) => string;
  /** The bars are a change and go below zero, so the number is said with its sign. */
  signed?: boolean;
}

interface SeriesChartProps {
  stats: UsersStats;
  title: string;
  color: string;
  mode: Mode;
  setMode: (mode: Mode) => void;
  series: Record<Mode, Series>;
}

/** One of the two charts: its name and modes, its total over the window and its bars. */
function SeriesChart({ stats, title, color, mode, setMode, series }: SeriesChartProps) {
  const { buckets, unit } = stats;
  const { total, value, valueLabel, detail, signed } = series[mode];
  const last = buckets.length - 1;
  // the bucket the moment of counting falls in, always the last one of a rolling window
  const getTitle = (index: number) => getBucketTitle(unit, buckets[index]!, index === last ? stats.now : null);
  const format = signed ? formatChange : formatCount;

  return (
    <div className="stats-chart">
      <div className="stats-chart__head">
        <h3 className="screen__title">{title}</h3>
        <div className="stats-chart__modes" role="group" aria-label={title}>
          {MODES.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`filters__chip stats-chart__mode${mode === option.value ? " filters__chip--active" : ""}`}
              aria-pressed={mode === option.value}
              onClick={() => setMode(option.value)}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
      </div>
      <p className="stats-chart__total">{total}</p>
      <BarChart
        values={buckets.map(value)}
        color={color}
        tickLabel={(index) => getTickLabel(unit, buckets, index)}
        ariaLabel={(index) => `${getTitle(index)}: ${format(value(buckets[index]!))}`}
        isFaded={(index) => index === last}
        details={(index) => (
          <>
            <span className="chart__popover-value">{valueLabel(buckets[index]!)}</span>
            {detail ? <span className="chart__popover-interval">{detail(buckets[index]!)}</span> : null}
            <span className="chart__popover-interval">{getTitle(index)}</span>
            {index === last ? <span className="chart__popover-note">{getCurrentNote(unit)}</span> : null}
          </>
        )}
      />
    </div>
  );
}

export function StatsScreen() {
  const stats = useUsersStats();
  const data = stats.stats;
  // each chart keeps its own mode, through a change of the window too; leaving the tab resets
  // both to the sums, the way the window goes back to a month
  const [newMode, setNewMode] = useState<Mode>("sum");
  const [activeMode, setActiveMode] = useState<Mode>("sum");

  return (
    <section className="screen">
      <h2 className="screen__title">{t("stats.title")}</h2>
      <p className="screen__hint">{t("stats.hint")}</p>

      {/* one row above both charts: the window scopes everything under it */}
      <div className="filters">
        {PERIODS.map((period) => (
          <button
            key={period.value}
            type="button"
            className={`filters__chip${stats.period === period.value ? " filters__chip--active" : ""}`}
            aria-pressed={stats.period === period.value}
            onClick={() => stats.setPeriod(period.value)}
          >
            {t(period.label)}
          </button>
        ))}
      </div>

      {stats.error ? (
        <>
          <Note kind="error">{stats.error}</Note>
          <button type="button" className="button" onClick={stats.retry}>
            {t("app.retry")}
          </button>
        </>
      ) : null}

      {data === null && stats.loading ? <p className="screen__loading">{t("stats.loading")}</p> : null}

      {data ? (
        // what was on the screen stays while the next window loads, dimmed rather than replaced
        // by a loading line, so the charts do not jump
        <div className={`stats${stats.loading ? " stats--loading" : ""}`} aria-busy={stats.loading}>
          <SeriesChart
            stats={data}
            title={t("stats.new.title")}
            color="var(--chart-new)"
            mode={newMode}
            setMode={setNewMode}
            series={{
              // the base: everybody registered by the end of the bucket. The window ends now,
              // so its last bucket holds everybody there is
              sum: {
                total: t("stats.new.base", { count: data.buckets.at(-1)!.total_users }),
                value: (bucket) => bucket.total_users,
                valueLabel: (bucket) => t("stats.new.base", { count: bucket.total_users }),
              },
              // and what it grew by in each bucket, which is the clients who registered in it
              change: {
                total: t("stats.new.total", { count: data.totals.new_users }),
                value: (bucket) => bucket.new_users,
                valueLabel: (bucket) => t("stats.new.value", { count: bucket.new_users }),
              },
            }}
          />
          <SeriesChart
            stats={data}
            title={t("stats.active.title")}
            color="var(--chart-active)"
            mode={activeMode}
            setMode={setActiveMode}
            series={{
              sum: {
                total: t("stats.active.total", { count: data.totals.active_users }),
                value: (bucket) => bucket.active_users,
                valueLabel: (bucket) => t("stats.active.value", { count: bucket.active_users }),
              },
              // a run of changes has no one honest total, so the line stays the unique clients
              change: {
                total: t("stats.active.total", { count: data.totals.active_users }),
                value: (bucket) => bucket.active_change,
                valueLabel: (bucket) => t("stats.active.change", { change: formatChange(bucket.active_change) }),
                detail: (bucket) =>
                  t("stats.active.changeDetail", {
                    count: bucket.active_users,
                    previous: getPreviousLabel(data.unit),
                    before: bucket.active_users - bucket.active_change,
                  }),
                signed: true,
              },
            }}
          />

          <p className="screen__hint">{t("stats.footnote")}</p>
        </div>
      ) : null}
    </section>
  );
}
